import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  Paper,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import alertNotificationStore from "../../stores/AlertNotificationStore";
import PriorityDegreeBar from "./PriorityDegreeBar";
import logStore from "../../stores/LogStore";
import deviceStore from "../../stores/DeviceStore";
import userStore from "../../stores/UserStore";

const AlertNotificationDetail = () => {
  const navigate = useNavigate();
  const { alertId } = useParams();

  const location = useLocation();
  const deviceId = location.state?.deviceId;
  const deviceName = location.state?.deviceName;

  const [device, setDevice] = useState(null);
  const [readStatusReady, setReadStatusReady] = useState(false);

  const alert = alertNotificationStore.getAlertById(alertId);

  const checkAdminStatus = () => {
    const storedSessionData = sessionStorage.getItem("userFinalSessionData");
    if (storedSessionData) {
      try {
        const parsedData = JSON.parse(storedSessionData);
        return (parsedData?.user?.roles || []).includes("Admin");
      } catch (error) {
        console.error("Error parsing user data from sessionStorage:", error);
      }
    }
    return false;
  };

  const isAdmin = checkAdminStatus();

  // const isAdmin = useMemo(() => {
  //   const roles = authStore.user?.roles || [];
  //   return Array.isArray(roles) && roles.includes("Admin");
  // }, [authStore.user]);

  console.log("Here is the alert: ", alert);
  console.log("isAdmin: ", isAdmin);
  console.log("User store: ", userStore.allUsers);
  // const device = alert
  //   ? deviceStore.devices.find((d) => d.id=== alert.device_id)
  //   : null;

  useEffect(() => {
    if (deviceId) {
      deviceStore.getDeviceByPimiosId(deviceId).then((d) => {
        setDevice(d);
        console.log("Device loaded for alert noti detail:", d);
      });
    }
  }, [deviceId]);

  useEffect(() => {
    const loadReadStatus = async () => {
      if (isAdmin) {
        if (userStore.allUsers.length === 0) {
          await userStore.fetchAllUsers();
        }

        await alertNotificationStore.fetchReadStatusForSpecificAlertId(alertId);
        setReadStatusReady(true); // ✅ trigger re-render
      }
    };

    loadReadStatus();
  }, [alertId, isAdmin]);

  // console.log("Alert status read by user map!", alertNotificationStore.alertStatusReadByUser.has(alertId));

  if (!alert) {
    return (
      <Box p={3}>
        <Typography color="error">No alert data found.</Typography>
      </Box>
    );
  }

  const rawDate = alert.last_notified_at?.$date || alert.last_notified_at;

  const formattedTime =
    rawDate && new Date(rawDate).toString() !== "Invalid Date"
      ? new Date(rawDate).toLocaleString("en-US", {
          timeZone: "America/New_York",
          hour12: true,
        })
      : "Not Available";

  return (
    <>
      <Box className="flex mb-2 gap-x-2" sx={{ alignItems: "center" }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          Alert Notification Details for {deviceName}
        </Typography>
      </Box>

      <PriorityDegreeBar degree={alert.priority} sx={{ mt: 5, mb: 5 }} />

      {isAdmin && (
        <Box sx={{ gridColumn: "span 2", mt: 2 }}>
          <Typography fontWeight="bold" gutterBottom>
            Users who read this alert:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {!readStatusReady ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton
                  key={idx}
                  variant="rounded"
                  width={80}
                  height={32}
                  sx={{ borderRadius: "16px" }}
                />
              ))
            ) : alertNotificationStore.getAlertReadByUserNames(alertId)
                .length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No users have read this alert yet.
              </Typography>
            ) : (
              alertNotificationStore
                .getAlertReadByUserNames(alertId)
                .map((userName, idx) => (
                  <Chip key={`${alertId}-${idx}`} label={userName} />
                ))
            )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          mt: 2,
        }}
      >
        {/* 🔹 Left Paper: Device Info */}
        {!device ? (
          // Skeleton fallback while device is being fetched
          <Paper
            elevation={3}
            sx={{
              flex: "1 1 19%",
              borderRadius: 3,
              minWidth: 200,
              p: 2,
              display: "flex",
              gap: 2,
            }}
          >
            <Box sx={{ width: 250, height: 160 }}>
              <Skeleton variant="rectangular" width={250} height={160} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
              <Divider sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" sx={{ mb: 2 }} />
              <Skeleton variant="text" width="70%" sx={{ mb: 2 }} />
              <Skeleton variant="text" width="90%" sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width={100} height={30} />
            </Box>
          </Paper>
        ) : (
          // ✅ Actual Device Box when ready
          <Paper
            elevation={3}
            sx={{
              flex: "1 1 19%",
              borderRadius: 3,
              display: "flex",
              overflow: "hidden",
              minWidth: 200,
            }}
          >
            {/* Image block */}
            <Box
              sx={{
                width: 250,
                height: "100%",
                backgroundColor: "#e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={
                  device.image_url ||
                  device.type_image ||
                  "https://placehold.co/220x150?text=No+Image"
                }
                alt={device.name || "Device image"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>

            {/* Device info text */}
            <Box sx={{ flex: 1, p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Device Information
              </Typography>

              <Divider sx={{ mb: 1 }} />

              <Typography sx={{ mb: 3 }}>
                <b>Name:</b> {device.name || "Unknown"}
              </Typography>

              <Typography sx={{ mb: 3 }}>
                <b>Alert Config ID:</b> {device.alert_config_id || "Unknown"}
              </Typography>

              <Typography sx={{ mb: 3 }}>
                <b>Location:</b>{" "}
                {device.location && typeof device.location === "object"
                  ? [
                      device.location.street,
                      device.location.city,
                      device.location.state,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : "Unknown"}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight="bold">Status:</Typography>
                <Chip
                  label={
                    logStore.connectedDeviceIds.has(device.pimios_id)
                      ? "Connected"
                      : "Not Connected"
                  }
                  color={
                    logStore.connectedDeviceIds.has(device.pimios_id)
                      ? "success"
                      : "error"
                  }
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Paper>
        )}

        {/* 🔸 Right Paper: Alert Details */}
        <Paper
          elevation={3}
          sx={{
            flex: "1 1 50%",
            borderRadius: 3,
            p: 3,
            minWidth: 350,
            backgroundColor: "#f9fbfd",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Alert Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              rowGap: 2,
              columnGap: 2,
            }}
          >
            <Typography>
              <strong>Message:</strong> {alert.triggerMessage || "No message"}
            </Typography>

            <Typography>
              <strong>Field:</strong> {alert.field}
            </Typography>

            <Typography>
              <strong>Type:</strong> {alert.alert_type}
            </Typography>

            <Typography>
              <strong>Triggered Value:</strong>{" "}
              <span style={{ color: "#d32f2f", fontWeight: 600 }}>
                {alert.value}
              </span>
            </Typography>

            <Typography sx={{ gridColumn: "span 2" }}>
              <strong>Expected:</strong>{" "}
              {typeof alert.expected === "string"
                ? alert.expected
                : JSON.stringify(alert.expected)}
            </Typography>

            <Typography sx={{ gridColumn: "span 2" }}>
              <strong>Last Notified:</strong> {formattedTime}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default AlertNotificationDetail;
